import { useImperativeHandle, useMemo, useRef, useState } from "react";
import type { JSX, Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";

import { DataTable } from "./DataTable";
import {
  type DataTableDataProps,
  dataTableDataSchema,
} from "./DataTable.schema";
import type {
  DataTableColumnFilter,
  DataTableRowMenuItem,
} from "./DataTable.types";
import { buildRWCColumns } from "./buildRWCColumns";
import type { RWCDataTableColumn, RWCDataTableRow } from "./buildRWCColumns";
import { DATA_TABLE_TAG_NAME } from "./constants";

export interface DataTableHandle {
  setColumns: (columns: RWCDataTableColumn[]) => void;
  setData: (data: RWCDataTableRow[]) => void;
  setSorting: (sorting: SortingState) => void;
  setColumnFilters: (filters: DataTableColumnFilter[]) => void;
  setRowMenu: (
    build: ((row: RWCDataTableRow) => DataTableRowMenuItem[]) | null,
  ) => void;
  setLoading: (loading: boolean) => void;
  setActiveRow: (index: number) => void;
  setSelection: (ids: string[]) => void;
  getSelection: () => string[];
  setSelectableRow: (
    predicate: ((row: RWCDataTableRow) => boolean) | null,
  ) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [DATA_TABLE_TAG_NAME]: {
      props: DataTableDataProps;
      handle: DataTableHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-data-table": HTMLElement & DataTableHandle;
  }
}

export function RWCDataTable({
  ref,
  size,
  variant,
  loading: initialLoading,
  loadingRowCount,
  loadingRowHeight,
  bordered,
  columnBorders,
  striped,
  scroll,
  maxHeight,
  stickyHeader,
  stickyOffset,
  stickyColumn,
  horizontalSpacing,
  verticalSpacing,
  emptyMessage,
  rowId = "id",
  selectable,
  enableMultiRowSelection,
  selectOnRowClick,
}: DataTableDataProps & { ref?: Ref<DataTableHandle> }): JSX.Element {
  const host = useIslandHost();

  const [columns, setColumns] = useState<RWCDataTableColumn[]>([]);
  const [data, setData] = useState<RWCDataTableRow[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<DataTableColumnFilter[]>(
    [],
  );
  const [loading, setLoading] = useState(initialLoading ?? false);
  const [activeRowIndex, setActiveRow] = useState(-1);
  const [hasRowMenu, setHasRowMenu] = useState(false);
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [selectableRow, setSelectableRow] = useState<
    ((row: RWCDataTableRow) => boolean) | null
  >(null);

  const rowMenuRef = useRef<
    ((row: RWCDataTableRow) => DataTableRowMenuItem[]) | null
  >(null);

  useImperativeHandle(
    ref,
    () => ({
      setColumns,
      setData,
      setSorting,
      setColumnFilters,
      setRowMenu: (build) => {
        rowMenuRef.current = build;
        setHasRowMenu(Boolean(build));
      },
      setLoading,
      setActiveRow,
      setSelection: (ids) => {
        setSelection(Object.fromEntries(ids.map((id) => [id, true])));
      },
      getSelection: () => Object.keys(selection),
      setSelectableRow: (predicate) => {
        setSelectableRow(() => predicate);
      },
    }),
    [selection],
  );

  const tanstackColumns = useMemo(() => buildRWCColumns(columns), [columns]);

  function handleSortingChange(nextSorting: SortingState) {
    setSorting(nextSorting);

    host?.dispatchEvent(
      new CustomEvent("data-table:sortingchange", {
        detail: { sorting: nextSorting },
        bubbles: true,
      }),
    );
  }

  function handleSortPrefetch(nextSorting: SortingState) {
    host?.dispatchEvent(
      new CustomEvent("data-table:sortprefetch", {
        detail: { sorting: nextSorting },
        bubbles: true,
      }),
    );
  }

  function handleColumnFiltersChange(nextFilters: DataTableColumnFilter[]) {
    setColumnFilters(nextFilters);

    host?.dispatchEvent(
      new CustomEvent("data-table:filterschange", {
        detail: { filters: nextFilters },
        bubbles: true,
      }),
    );
  }

  function handleRowMenuSelect(id: string, row: RWCDataTableRow) {
    host?.dispatchEvent(
      new CustomEvent("data-table:rowmenuselect", {
        detail: { id, row },
        bubbles: true,
      }),
    );
  }

  function getRowId(row: RWCDataTableRow, index: number): string {
    const id = row[rowId];

    return "string" === typeof id || "number" === typeof id
      ? String(id)
      : String(index);
  }

  function handleRowSelectionChange(nextSelection: RowSelectionState) {
    setSelection(nextSelection);

    const ids = Object.keys(nextSelection);

    host?.dispatchEvent(
      new CustomEvent("data-table:selectionchange", {
        detail: {
          ids,
          rows: data.filter((row, index) =>
            Boolean(nextSelection[getRowId(row, index)]),
          ),
        },
        bubbles: true,
      }),
    );
  }

  function handleRowClick(row: RWCDataTableRow) {
    host?.dispatchEvent(
      new CustomEvent("data-table:rowclick", {
        detail: { row },
        bubbles: true,
      }),
    );
  }

  return (
    <DataTable
      columns={tanstackColumns}
      data={data}
      getRowId={getRowId}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      onSortPrefetch={handleSortPrefetch}
      columnFilters={columnFilters}
      onColumnFiltersChange={handleColumnFiltersChange}
      onRowClick={handleRowClick}
      rowSelection={selection}
      onRowSelectionChange={selectable ? handleRowSelectionChange : undefined}
      enableRowSelection={selectableRow ?? true}
      enableMultiRowSelection={enableMultiRowSelection}
      selectOnRowClick={selectOnRowClick}
      activeRowIndex={activeRowIndex}
      rowMenu={
        hasRowMenu ? (row) => rowMenuRef.current?.(row) ?? [] : undefined
      }
      onRowMenuSelect={handleRowMenuSelect}
      loading={loading}
      loadingRowCount={loadingRowCount}
      loadingRowHeight={loadingRowHeight}
      bordered={bordered}
      columnBorders={columnBorders}
      striped={striped}
      scroll={scroll}
      maxHeight={maxHeight}
      stickyHeader={stickyHeader}
      stickyOffset={stickyOffset}
      stickyColumn={stickyColumn}
      horizontalSpacing={horizontalSpacing}
      verticalSpacing={verticalSpacing}
      emptyMessage={emptyMessage}
      size={size}
      variant={variant}
    />
  );
}

componentManager.register(
  rwc(DATA_TABLE_TAG_NAME, {
    component: RWCDataTable,
    dataSchema: dataTableDataSchema,
  }),
);
